import { Kaggle } from "@kaggle/sdk";
import { getServices } from "../../../../api/getServices";
import { appendTerminalError } from "../../actions/terminal";
import { getStore } from "../../store/appStore";
import {
  setDockerImage,
  setDockerImages,
  setDockerPinningType,
} from "../notebook";
import { restartWorker } from "../worker";

import DockerImagePinningType = Kaggle.Sdk.Kernels.DockerImagePinningType;
import DockerImageVersionDisabledReason = Kaggle.Sdk.Kernels.DockerImageVersionDisabledReason;
import DockerImageVersionType = Kaggle.Sdk.Kernels.DockerImageVersionType;

/**
 * The ID to use when docker image is not pinned to a specific version.
 *
 * Legacy was called unpinnedDockerImageVersionId.
 */
export const LATEST_DOCKER_IMAGE_ID = -1;

export const getDockerImages = async (kernelSessionId: number) => {
  const services = getServices();

  // Clear previously loaded images.
  setDockerImages([], DockerImagePinningType.ORIGINAL);

  if (!kernelSessionId) {
    return;
  }

  // For now, listDockerImages only returns empty or a single image if restricted for a competition.
  // Eventually, we may allow the selection of arbitrary images.
  const response = await services.kernelsClient.kernelsService.listDockerImages(
    {
      kernelSessionId,
    }
  );

  function toVersionId(
    versionType: DockerImageVersionType,
    versionId: number | null | undefined
  ): number | DockerImagePinningType {
    switch (versionType) {
      case DockerImageVersionType.COMPETITION:
        return versionId || DockerImagePinningType.ORIGINAL; // defaults to original. should never happen.
      case DockerImageVersionType.ORIGINAL:
        return DockerImagePinningType.ORIGINAL;
      case DockerImageVersionType.LATEST:
        return DockerImagePinningType.LATEST;
    }
  }

  const images = response.images.map(image => {
    let name: string;
    let description: string;
    switch (image.type) {
      case DockerImageVersionType.ORIGINAL:
        name = "Pin to original environment";
        description =
          "You won't get new packages, but your code is less likely to break.";
        break;
      case DockerImageVersionType.LATEST:
        name = "Always use latest environment";
        description =
          "Always get the latest package versions, but you may have to modify your code.";
        break;
      case DockerImageVersionType.COMPETITION:
        name = "Competition environment";
        description =
          "This notebook's competition restricts the environment this notebook can run in.";
        break;
    }

    let disabledReason: string;
    let disabled: boolean;
    switch (image.disabledReason) {
      case DockerImageVersionDisabledReason.TPU_RESTRICTION:
        disabledReason =
          "Sessions using a TPU accelerator can only run in the latest environment.";
        disabled = true;
        break;
      default:
        disabledReason = "";
        disabled = false;
        break;
    }

    return {
      versionId: toVersionId(image.type, image.id),
      name,
      description,
      disabled,
      disabledReason,
      dateCreated: image.dateCreated,
    };
  });
  setDockerImages(
    images,
    toVersionId(response.selectedVersionType, response.selectedVersionId)
  );
};

/**
 * Updates the currently selected docker image in database / Redux.
 */
export const updateDockerImage = async (imageId: number) => {
  const services = getServices();
  const state = getStore().getState();
  try {
    const kernelId = state.notebook.kernel;
    if (kernelId === null) {
      throw new Error(
        "KernelId should not be null when updating docker image."
      );
    }
    await services.kernelsClient.kernelsService.updateDockerImageVersion({
      scriptId: kernelId,
      dockerImageVersionId:
        imageId === LATEST_DOCKER_IMAGE_ID ? undefined : imageId,
    });
    setDockerImage(imageId);
    await restartWorker();
  } catch (error) {
    appendTerminalError("An error occurred updating the docker image.", error);
    throw error;
  }
};

/*
 * Updates the type of docker image pinning in the DB and redux for current kernel.
 */
export const updateDockerPinningType = async (
  dockerImagePinningType: DockerImagePinningType
) => {
  const services = getServices();
  const state = getStore().getState();

  try {
    const kernelId = state.notebook.kernel;
    if (kernelId === null) {
      throw new Error(
        "KernelId should not be null when updating docker image."
      );
    }

    setDockerPinningType(dockerImagePinningType);

    await services.kernelsClient.kernelsService.updateDockerPinningType({
      kernelId,
      dockerImagePinningType,
    });

    await restartWorker();
  } catch (error) {
    appendTerminalError("An error occurred updating the docker image.", error);
    throw error;
  }
};
